import React, { useEffect, useRef } from 'react';
import { StyleSheet, Animated, TouchableOpacity } from 'react-native';

import { Box } from '@components/Restyle/index';
import Text from '@components/Restyle/Text';
import Icon from '@components/shared/Icon/Icon';
import theme, { Theme } from '@themes/theme';

type StatusToastProps = {
  duration: number;
  onPress?: () => void;
  message?: string;
  type?: 'success' | 'error';
  // Lets a caller make the icon stand out more; defaults to the white icon.
  iconColor?: keyof Theme['colors'];
};

export const StatusToast: React.FC<StatusToastProps> = ({
  duration,
  onPress,
  message,
  type = 'success',
  iconColor = 'white',
}) => {
  const progressAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 1,
      duration: duration,
      useNativeDriver: false,
    }).start();
  }, [progressAnim, duration]);

  const handlePress = () => {
    onPress?.();
  };

  const isError = type === 'error';
  const iconName = isError ? 'AlertTriangle' : 'Check';
  const progressBarColor = isError
    ? theme.colors.alertRed
    : theme.colors.greenBrand;

  return (
    <TouchableOpacity
      disabled={!onPress}
      onPress={handlePress}
      accessibilityLabel={message}>
      <Box
        height={80}
        backgroundColor='black'
        style={{
          overflow: 'hidden',
          borderRadius: 8,
          display: 'flex',
          paddingHorizontal: 12,
          position: 'relative',
          paddingVertical: 22,
        }}>
        <Box
          mx='l'
          style={{ justifyContent: 'center' }}
          flexDirection='row'
          alignItems='center'>
          <Icon name={iconName} variant='l' color={iconColor} />
          <Text ml='xs' variant='heading2' color='white'>
            {message}
          </Text>
          {!!onPress && <Icon name='ArrowRight' variant='l' color='white' />}
        </Box>

        <Animated.View
          style={[
            styles.progressBar,
            {
              backgroundColor: progressBarColor,
              width: progressAnim.interpolate({
                inputRange: [0, 1],
                outputRange: ['0%', '100%'],
              }),
            },
          ]}
        />
      </Box>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  progressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 4,
  },
});
